import React, { JSX } from "react";
import { cn } from "../../utils/cn";

interface TypographyProps {
  children: React.ReactNode;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
}

export const Display1: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "h1",
}) => (
  <Component className={cn("text-[72px] font-bold text-white", className)}>
    {children}
  </Component>
);

export const Display2: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "h2",
}) => (
  <Component
    className={cn(
      "text-[64px] leading-[122px] font-bold text-white",
      className,
    )}
  >
    {children}
  </Component>
);

export const Display3: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "h3",
}) => (
  <Component
    className={cn(
      "text-[56px] leading-[122px] font-bold text-white",
      className,
    )}
  >
    {children}
  </Component>
);

// Heading Components
export const Heading1: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "h1",
}) => (
  <Component className={cn("text-[40px] font-bold text-white", className)}>
    {children}
  </Component>
);

export const Heading2: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "h2",
}) => (
  <Component
    className={cn(
      "text-[36px] leading-[122px] font-bold text-white",
      className,
    )}
  >
    {children}
  </Component>
);

export const Heading3: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "h3",
}) => (
  <Component className={cn("text-[32px] font-bold text-white", className)}>
    {children}
  </Component>
);

export const Heading4: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "h4",
}) => (
  <Component className={cn("text-[28px] font-bold text-white", className)}>
    {children}
  </Component>
);

export const Heading5: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "h5",
}) => (
  <Component
    className={cn(
      "text-[24px] leading-[125px] font-bold text-white",
      className,
    )}
  >
    {children}
  </Component>
);

export const Heading6: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "h6",
}) => (
  <Component className={cn("text-[20px] font-bold text-white", className)}>
    {children}
  </Component>
);

// Body Huge Components
export const BodyHugeRegular: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component className={cn("text-[20px] font-normal text-gray-300", className)}>
    {children}
  </Component>
);

export const BodyHugeMedium: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component className={cn("text-[20px] font-medium text-gray-300", className)}>
    {children}
  </Component>
);

export const BodyHugeSemibold: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[20px] font-semibold text-gray-300", className)}
  >
    {children}
  </Component>
);

export const BodyHugeBold: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component className={cn("text-[20px] font-bold text-gray-300", className)}>
    {children}
  </Component>
);

// Body Huge All Caps Variants
export const BodyHugeRegularCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[20px] font-normal uppercase text-gray-300", className)}
  >
    {children}
  </Component>
);

export const BodyHugeMediumCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[20px] font-medium uppercase text-gray-300", className)}
  >
    {children}
  </Component>
);

export const BodyHugeSemiboldCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn(
      "text-[20px] font-semibold uppercase text-gray-300",
      className,
    )}
  >
    {children}
  </Component>
);

export const BodyHugeBoldCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[20px] font-bold uppercase text-gray-300", className)}
  >
    {children}
  </Component>
);

// Body Large Components
export const BodyLargeRegular: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component className={cn("text-[18px] font-normal text-gray-300", className)}>
    {children}
  </Component>
);

export const BodyLargeMedium: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component className={cn("text-[18px] font-medium text-gray-300", className)}>
    {children}
  </Component>
);

export const BodyLargeSemibold: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[18px] font-semibold text-gray-300", className)}
  >
    {children}
  </Component>
);

export const BodyLargeBold: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component className={cn("text-[18px] font-bold text-gray-300", className)}>
    {children}
  </Component>
);

// Body Large All Caps Variants
export const BodyLargeRegularCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[18px] font-normal uppercase text-gray-300", className)}
  >
    {children}
  </Component>
);

export const BodyLargeMediumCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[18px] font-medium uppercase text-gray-300", className)}
  >
    {children}
  </Component>
);

export const BodyLargeSemiboldCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn(
      "text-[18px] font-semibold uppercase text-gray-300",
      className,
    )}
  >
    {children}
  </Component>
);

export const BodyLargeBoldCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[18px] font-bold uppercase text-gray-300", className)}
  >
    {children}
  </Component>
);

// Body Normal Components
export const BodyNormalRegular: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component className={cn("text-[16px] font-normal text-gray-300", className)}>
    {children}
  </Component>
);

export const BodyNormalMedium: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component className={cn("text-[16px] font-medium text-gray-300", className)}>
    {children}
  </Component>
);

export const BodyNormalSemibold: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[16px] font-semibold text-gray-300", className)}
  >
    {children}
  </Component>
);

export const BodyNormalBold: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component className={cn("text-[16px] font-bold text-gray-300", className)}>
    {children}
  </Component>
);

// Body Normal All Caps Variants
export const BodyNormalRegularCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[16px] font-normal uppercase text-gray-300", className)}
  >
    {children}
  </Component>
);

export const BodyNormalMediumCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[16px] font-medium uppercase text-gray-300", className)}
  >
    {children}
  </Component>
);

export const BodyNormalSemiboldCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn(
      "text-[16px] font-semibold uppercase text-gray-300",
      className,
    )}
  >
    {children}
  </Component>
);

export const BodyNormalBoldCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[16px] font-bold uppercase text-gray-300", className)}
  >
    {children}
  </Component>
);

// Body Small Components
export const BodySmallRegular: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component className={cn("text-[14px] font-normal text-gray-300", className)}>
    {children}
  </Component>
);

export const BodySmallMedium: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component className={cn("text-[14px] font-medium text-gray-300", className)}>
    {children}
  </Component>
);

export const BodySmallSemibold: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[14px] font-semibold text-gray-300", className)}
  >
    {children}
  </Component>
);

export const BodySmallBold: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component className={cn("text-[14px] font-bold text-gray-300", className)}>
    {children}
  </Component>
);

// Body Small All Caps Variants
export const BodySmallRegularCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[14px] font-normal uppercase text-gray-300", className)}
  >
    {children}
  </Component>
);

export const BodySmallMediumCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[14px] font-medium uppercase text-gray-300", className)}
  >
    {children}
  </Component>
);

export const BodySmallSemiboldCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn(
      "text-[14px] font-semibold uppercase text-gray-300",
      className,
    )}
  >
    {children}
  </Component>
);

export const BodySmallBoldCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[14px] font-bold uppercase text-gray-300", className)}
  >
    {children}
  </Component>
);

// Body Tiny Components
export const BodyTinyRegular: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component className={cn("text-[12px] font-normal text-gray-300", className)}>
    {children}
  </Component>
);

export const BodyTinyMedium: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component className={cn("text-[12px] font-medium text-gray-300", className)}>
    {children}
  </Component>
);

export const BodyTinySemibold: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[12px] font-semibold text-gray-300", className)}
  >
    {children}
  </Component>
);

export const BodyTinyBold: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component className={cn("text-[12px] font-bold text-gray-300", className)}>
    {children}
  </Component>
);

// Body Tiny All Caps Variants
export const BodyTinyRegularCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[12px] font-normal uppercase text-gray-300", className)}
  >
    {children}
  </Component>
);

export const BodyTinyMediumCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[12px] font-medium uppercase text-gray-300", className)}
  >
    {children}
  </Component>
);

export const BodyTinySemiboldCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn(
      "text-[12px] font-semibold uppercase text-gray-300",
      className,
    )}
  >
    {children}
  </Component>
);

export const BodyTinyBoldCaps: React.FC<TypographyProps> = ({
  children,
  className,
  as: Component = "p",
}) => (
  <Component
    className={cn("text-[12px] font-bold uppercase text-gray-300", className)}
  >
    {children}
  </Component>
);
